import { FC } from 'react';

import { Badge, Link } from '@purpur/library';

import { Checkbox } from '@telialt/common-checkbox/src';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useInsuranceDisclaimer } from '@telialt/core-eshop/src/hooks';
import { useInsuranceMutation } from '@telialt/core-eshop/src/hooks/mutations/useInsuranceMutation';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import {
    selectInsuranceFreePeriod,
    selectInsuranceMonthlyPrice,
    selectIsInsurancePossible,
    selectWithInsurance,
} from '@telialt/utils/src/eshopSelectors';
import { withParams } from '@telialt/utils/src/helpers';

import { InsuranceVAS } from './InsuranceVAS';
import {
    BlueInfoIcon,
    InsuranceCheckboxContainer,
    InsuranceDetailsBlock,
    InsuranceDetailsExplanation,
    InsurancePriceLabelContainer,
    InsuranceSubtext,
    InsuranceTitle,
    InsuranceTooltipPointer,
    InsuranceTooltipWrapper,
    InsuranceWrapper,
    PriceContainer,
    TitleWrapper,
} from './ProductInsurance.styles';

const BADGE_TITLE_KEY = 'eshop.product_page.product_insurance.badge.title';

export const ProductInsurance: FC = () => {
    const { t, hasMessage } = useTranslations();

    const { cart, cartIsLoading } = useCartQuery();
    const { mutateInsurance, insuranceIsLoading } = useInsuranceMutation();
    const isLoaderActive = cartIsLoading || insuranceIsLoading;

    const tooltipContent = useInsuranceDisclaimer(t['eshop.product_page.product_insurance.tooltip.content'], cart);

    const withInsurance = selectWithInsurance(cart);
    const isInsurancePossible = selectIsInsurancePossible(cart);
    const insuranceFreePeriod = selectInsuranceFreePeriod(cart);
    const insurancePrice = selectInsuranceMonthlyPrice(cart);

    const handleCheckboxChange = () => mutateInsurance(!withInsurance);

    if (!isInsurancePossible) {
        return null;
    }

    return (
        <InsuranceWrapper data-test={ESHOP_TEST_ID.PRODUCT_INSURANCE_BLOCK}>
            <TitleWrapper>
                <InsuranceTitle tag="h3" variant="title-100">
                    {t['eshop.product_page.product_insurance.title']}
                </InsuranceTitle>
                {hasMessage(BADGE_TITLE_KEY) && (
                    <Badge variant="attention" showIcon={false} data-test={ESHOP_TEST_ID.PRODUCT_INSURANCE_BADGE}>
                        {t[BADGE_TITLE_KEY]}
                    </Badge>
                )}
            </TitleWrapper>
            <InsuranceCheckboxContainer>
                <Checkbox
                    dataTest={ESHOP_TEST_ID.PRODUCT_INSURANCE_CHECKBOX}
                    checked={withInsurance}
                    name="productInsuranceCheckbox"
                    disabled={isLoaderActive}
                    onChange={handleCheckboxChange}
                    label={t['eshop.product_page.product_insurance.checkbox.label']}
                />
                <PriceContainer>
                    <InsurancePriceLabelContainer>
                        <strong aria-label={t['eshop.product_page.product_insurance.zero.payment.amount_accessible']}>
                            <span aria-hidden={true}>
                                {t['eshop.product_page.product_insurance.zero.payment.amount']}
                            </span>
                        </strong>
                        <InsuranceTooltipPointer
                            content={tooltipContent}
                            id="insurance-tooltip-content"
                            ariaLabel={t['common.tooltip.aria_label']}
                        >
                            <InsuranceTooltipWrapper>
                                <BlueInfoIcon />
                            </InsuranceTooltipWrapper>
                        </InsuranceTooltipPointer>
                    </InsurancePriceLabelContainer>
                    <InsuranceSubtext>
                        {withParams(t['eshop.product_page.product_insurance.payment.amount'], {
                            months: insuranceFreePeriod + 1,
                            monthlyPrice: insurancePrice?.formattedValue,
                        })}
                    </InsuranceSubtext>
                </PriceContainer>
            </InsuranceCheckboxContainer>
            <InsuranceVAS />
            <InsuranceDetailsBlock>
                <Link
                    variant="text"
                    href={t['eshop.product_page.product_insurance.details.link']}
                    target="_blank"
                    disableVisited={true}
                >
                    {t['eshop.product_page.product_insurance.details.title']}
                </Link>
                <InsuranceDetailsExplanation>
                    {t['eshop.product_page.product_insurance.details.explanation']}
                </InsuranceDetailsExplanation>
            </InsuranceDetailsBlock>
        </InsuranceWrapper>
    );
};
